import { el, state } from "./core.js"
import {
  rebuildLoopingPreview as rebuildPreviewFrames,
  seekPreviewToSourceTime,
  setupPreviewEngine as setupPreviewRenderer,
  stopPreviewEngine as stopPreviewRenderer,
  togglePreviewPlayback,
} from "./preview-engine.js"

const STORAGE_KEY = "videoConverterAutoPreview"
let activeBuild = null
let rebuildTimer = 0
let initialized = false
let suspendedForConversion = false
let autoPreviewEnabled = true

const byId = (id) => document.getElementById(id)

function loadPreference() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved == null ? true : saved !== "false"
  } catch {
    return true
  }
}

function savePreference() {
  try {
    localStorage.setItem(STORAGE_KEY, String(autoPreviewEnabled))
  } catch {
    // Storage can be unavailable.
  }
}

function hidePreviewLoading() {
  const overlay = byId("previewLoading")
  if (overlay) overlay.hidden = true
}

function updateControls() {
  const toggle = byId("autoPreviewToggle")
  const helper = byId("autoPreviewHelper")
  const stopButton = byId("stopPreviewButton")
  const pending = Boolean(rebuildTimer)
  const building = Boolean(activeBuild)
  if (toggle) toggle.checked = autoPreviewEnabled
  if (helper) helper.textContent = suspendedForConversion
    ? "Suspended while conversion uses the decoder and workers"
    : building
      ? "Processing preview now"
      : pending
        ? "Preview rebuild queued"
        : autoPreviewEnabled
          ? "Rebuild after crop, trim, direction, or output-setting changes"
          : "Off — conversion resources are not used for preview"
  if (stopButton) {
    stopButton.disabled = !state.file || (!autoPreviewEnabled && !building && !pending)
    stopButton.textContent = building || pending ? "Stop preview" : "Disable preview"
  }
}

async function cancelActiveBuild() {
  clearTimeout(rebuildTimer)
  rebuildTimer = 0
  state.previewBuildToken += 1
  hidePreviewLoading()
  const pending = activeBuild
  updateControls()
  if (pending) {
    try { await pending } catch { /* Superseded previews abort normally. */ }
  }
}

export function isAutoPreviewEnabled() {
  return autoPreviewEnabled
}

export async function setAutoPreviewEnabled(enabled, options = {}) {
  autoPreviewEnabled = Boolean(enabled)
  savePreference()
  if (!autoPreviewEnabled) {
    await cancelActiveBuild()
    if (options.announce !== false && state.file) el.statusText.textContent = "Preview stopped. Auto preview is off."
  } else if (!suspendedForConversion && !state.encoding && state.file && state.videoTrack) {
    schedulePreviewRebuild(options.delay ?? 0)
  }
  updateControls()
}

export async function rebuildLoopingPreview(options = {}) {
  clearTimeout(rebuildTimer)
  rebuildTimer = 0
  const force = Boolean(options.force)
  if (!state.file || !state.videoTrack || state.encoding || suspendedForConversion) {
    hidePreviewLoading()
    updateControls()
    return
  }
  if (!force && !autoPreviewEnabled) {
    hidePreviewLoading()
    el.statusText.textContent = "Video loaded. Auto preview is off."
    updateControls()
    return
  }
  if (activeBuild) await cancelActiveBuild()
  const promise = rebuildPreviewFrames()
  activeBuild = promise
  updateControls()
  try {
    await promise
  } finally {
    if (activeBuild === promise) activeBuild = null
    updateControls()
  }
}

export function schedulePreviewRebuild(delay = 180) {
  clearTimeout(rebuildTimer)
  rebuildTimer = 0
  if (!autoPreviewEnabled || suspendedForConversion || state.encoding || !state.file || !state.videoTrack) {
    hidePreviewLoading()
    updateControls()
    return
  }
  rebuildTimer = setTimeout(() => {
    rebuildTimer = 0
    void rebuildLoopingPreview().catch((error) => {
      if (error?.name === "AbortError") return
      el.statusText.textContent = error instanceof Error ? error.message : String(error)
      el.errorBox.textContent = el.statusText.textContent
      el.errorBox.hidden = false
    })
  }, Math.max(0, delay))
  updateControls()
}

export async function suspendPreviewForConversion() {
  suspendedForConversion = true
  await cancelActiveBuild()
  hidePreviewLoading()
  updateControls()
}

export function resumePreviewAfterConversion(options = {}) {
  suspendedForConversion = false
  updateControls()
  if (options.rebuild && autoPreviewEnabled && state.file && state.videoTrack) {
    schedulePreviewRebuild(options.delay ?? 180)
  }
}

export function setupPreviewEngine() {
  if (initialized) return
  initialized = true
  autoPreviewEnabled = loadPreference()
  setupPreviewRenderer()
  const toggle = byId("autoPreviewToggle")
  const stopButton = byId("stopPreviewButton")
  if (toggle) {
    toggle.checked = autoPreviewEnabled
    toggle.addEventListener("change", () => void setAutoPreviewEnabled(toggle.checked))
  }
  stopButton?.addEventListener("click", () => void setAutoPreviewEnabled(false))
  addEventListener("converter-plan-change", (event) => schedulePreviewRebuild(event.detail?.reason === "crop" ? 80 : 180))
  addEventListener("converter-crop-change", () => schedulePreviewRebuild(40))
  updateControls()
}

export function stopPreviewEngine() {
  clearTimeout(rebuildTimer)
  rebuildTimer = 0
  suspendedForConversion = true
  state.previewBuildToken += 1
  activeBuild = null
  hidePreviewLoading()
  stopPreviewRenderer()
  updateControls()
}

export { seekPreviewToSourceTime, togglePreviewPlayback }
